import { Paging } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { BussinessAppUserInjection } from "features/businessAppUserManagement/di/BussinessAppUserInjection";
import { BussinessAppUserRequestEntity } from "features/businessAppUserManagement/domain/entities/BussinessAppUserRequestEntity";
import { BussinessAppUserResponseEntity } from "features/businessAppUserManagement/domain/entities/BussinessAppUserResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";

// ─── Input ────────────────────────────────────────────────────────────────────

class BusinessUserAppInput implements Disposable {
  public fetchList = new BehaviorSubject<BussinessAppUserRequestEntity>({
    page: 0,
    limit: 10,
    platform: "app",
  });
  public fetchDetail = new BehaviorSubject<string>("");
  public resetOtp = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.resetOtp.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────

class BusinessUserAppOutput implements Disposable {
  public savedList = new BehaviorSubject<Array<BussinessAppUserResponseEntity>>(
    [],
  );
  public savedDetail =
    new BehaviorSubject<BussinessAppUserResponseEntity | null>(null);
  public pagination = new BehaviorSubject<Paging>({
    page: 0,
    limit: 10,
    total: 0,
  });
  public listLoading = new BehaviorSubject<boolean>(false);
  public detailLoading = new BehaviorSubject<boolean>(false);
  public resetOtpLoading = new BehaviorSubject<boolean>(false);
  public onError = new BehaviorSubject<string>("");
  public actionSuccess = new BehaviorSubject<"resetOtp" | null>(null);

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.savedDetail.complete();
    this.pagination.complete();
    this.listLoading.complete();
    this.detailLoading.complete();
    this.resetOtpLoading.complete();
    this.onError.complete();
    this.actionSuccess.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class BusinessUserAppManagementViewModel extends BaseViewModel<
  BusinessUserAppInput,
  BusinessUserAppOutput
> {
  constructor() {
    super(new BusinessUserAppInput(), new BusinessUserAppOutput());
  }

  private useCase = BussinessAppUserInjection.getUseCase();

  protected override transform(): void {
    // Fetch user list
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.output.listLoading.next(true);
        const res = await this.useCase.getUsers(params);
        if (res?.data) {
          this.output.savedList.next(res.data);
          this.output.pagination.next(res.pagination);
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.listLoading.next(false);
      }
    });

    // Fetch user detail
    this.skipFirstInitial(this.input.fetchDetail, async (phone) => {
      if (!phone) return;
      try {
        this.output.detailLoading.next(true);
        const res = await this.useCase.getBusinessAppUserDetail({ phone });
        if (res?.data) {
          this.output.savedDetail.next(res.data);
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.detailLoading.next(false);
      }
    });

    // Reset OTP
    this.skipFirstInitial(this.input.resetOtp, async (phone) => {
      if (!phone) return;
      try {
        this.output.resetOtpLoading.next(true);
        const res = await this.useCase.resetOtpUser(phone);
        if (res?.data) {
          this.output.actionSuccess.next("resetOtp");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.resetOtpLoading.next(false);
      }
    });
  }
}
